import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { BrandOnboarding } from './pages/onboarding/BrandOnboarding';
import { OrganizerOnboarding } from './pages/onboarding/OrganizerOnboarding';
import { BrandDashboard } from './pages/dashboard/BrandDashboard';
import { OrganizerDashboard } from './pages/dashboard/OrganizerDashboard';
import { MatchDetails } from './pages/dashboard/MatchDetails';
import { MatchResultsPage } from './pages/dashboard/MatchResultsPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { LoginTroubleshooting } from './pages/LoginTroubleshooting';
import { MessagesPage } from './pages/dashboard/MessagesPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DraftProfileProvider } from './context/DraftProfileContext';
import { HelpChat } from './components/HelpChat';
import { initializeCollaborations } from './services/collaborationService';
import { initEmailJS } from './services/emailService';
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
import { BrandsDirectoryPage } from './pages/BrandsDirectoryPage';
import { OrganizersDirectoryPage } from './pages/OrganizersDirectoryPage';
function ProtectedRoute({
  children,
  requiredType,
  allowedRoles
}: {
  children: React.ReactNode;
  requiredType?: 'brand' | 'organizer' | 'admin';
  allowedRoles?: string[];
}) {
  const {
    currentUser
  } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-0" />;
  }
  if (allowedRoles && !allowedRoles.includes(currentUser.type)) {
    return <Navigate to="/" replace data-id="element-1" />;
  }
  if (requiredType && currentUser.type !== requiredType) {
    if (currentUser.type === 'brand') {
      return <Navigate to="/dashboard/brand" replace data-id="element-2" />;
    } else if (currentUser.type === 'organizer') {
      return <Navigate to="/dashboard/organizer" replace data-id="element-3" />;
    } else {
      return <Navigate to="/admin" replace data-id="element-4" />;
    }
  }
  return <>{children}</>;
}
function AppRoutes() {
  useEffect(() => {
    initializeCollaborations();
    initEmailJS();
  }, []);
  return <Routes data-id="element-5">
      <Route path="/" element={<Home data-id="element-7" />} data-id="element-6" />

      {/* Public Directories */}
      <Route path="/brands" element={<BrandsDirectoryPage data-id="element-9" />} data-id="element-8" />
      <Route path="/organizers" element={<OrganizersDirectoryPage data-id="element-11" />} data-id="element-10" />

      {/* Onboarding */}
      <Route path="/onboarding/brand" element={<BrandOnboarding data-id="element-13" />} data-id="element-12" />
      <Route path="/onboarding/organizer" element={<OrganizerOnboarding data-id="element-15" />} data-id="element-14" />

      {/* Auth */}
      <Route path="/login" element={<Login data-id="element-17" />} data-id="element-16" />
      <Route path="/login/help" element={<LoginTroubleshooting data-id="element-19" />} data-id="element-18" />
      <Route path="/register" element={<Register data-id="element-21" />} data-id="element-20" />

      {/* Brand Dashboard */}
      <Route path="/dashboard/brand" element={<ProtectedRoute requiredType="brand" data-id="element-23">
            <BrandDashboard data-id="element-24" />
          </ProtectedRoute>} data-id="element-22" />

      {/* Organizer Dashboard */}
      <Route path="/dashboard/organizer" element={<ProtectedRoute requiredType="organizer" data-id="element-26">
            <OrganizerDashboard data-id="element-27" />
          </ProtectedRoute>} data-id="element-25" />

      {/* Shared Dashboard Routes */}
      <Route path="/dashboard/matches" element={<ProtectedRoute data-id="element-29">
            <MatchResultsPage data-id="element-30" />
          </ProtectedRoute>} data-id="element-28" />
      <Route path="/dashboard/matches/:matchId" element={<ProtectedRoute data-id="element-32">
            <MatchDetails data-id="element-33" />
          </ProtectedRoute>} data-id="element-31" />
      <Route path="/dashboard/messages" element={<ProtectedRoute data-id="element-35">
            <MessagesPage data-id="element-36" />
          </ProtectedRoute>} data-id="element-34" />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute requiredType="admin" data-id="element-38">
            <AdminDashboard data-id="element-39" />
          </ProtectedRoute>} data-id="element-37" />
      <Route path="/admin/super" element={<ProtectedRoute allowedRoles={['super_admin']} data-id="element-41">
            <SuperAdminDashboard data-id="element-42" />
          </ProtectedRoute>} data-id="element-40" />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace data-id="element-44" />} data-id="element-43" />
    </Routes>;
}
export function App() {
  return <AuthProvider data-id="element-45">
      <DraftProfileProvider data-id="element-46">
        <BrowserRouter data-id="element-47">
          <AppRoutes data-id="element-48" />
          <HelpChat data-id="element-49" />
        </BrowserRouter>
      </DraftProfileProvider>
    </AuthProvider>;
}